import React, { useState, useRef,useEffect,useMemo } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import { Button, Table, Modal, Spinner, Form, InputGroup } from "react-bootstrap";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import moment from "moment";
import EditAttendanceModal from "./EditAttendanceModal";
import { API_URL } from "../..";
import PayrollPDFWeekly from "./PayrollPDFWeekly";
import { PDFDownloadLink, PDFViewer } from "@react-pdf/renderer";
import Switch from 'react-switch'
import Swal from "sweetalert2";
const TimesheetPayrollSlipWeekly = ({ date, employee, data }) => {
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [showLockConfirm, setShowLockConfirm] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const tableRef = useRef();
  const [addTip, setAddTip] = useState(false);
  const [tipAmount, setTipAmount] = useState("");
  const [isLocking, setIsLocking] = useState(false);
  const startOfWeek = moment(date).startOf("isoWeek");
  const endOfWeek = moment(startOfWeek).clone().add(6, "days");

  const weekDays = Array.from({ length: 7 }, (_, i) =>
    moment(startOfWeek).clone().add(i, "days")
  );

  const { user, attendance = [] } = data || {};
  const [summary, setSummary] = useState(data?.summary || {});
  const [records, setRecords] = useState(attendance);

  // New state for deductions
  const [showDeductionModal, setShowDeductionModal] = useState(false);
  const [deductionType, setDeductionType] = useState("dayByDay"); // "fixed" or "dayByDay"
  const [fixedDeduction, setFixedDeduction] = useState(0);
  const [dayDeductions, setDayDeductions] = useState(weekDays.map(day => ({
    date: day.format("YYYY-MM-DD"),
    day: day.format("dddd"),
    amount: 0,
  })));
  const [isSubmittingDeduction, setIsSubmittingDeduction] = useState(false);
  const [deductionAmount, setDeductionAmount] = useState(0);
  useEffect(() => {
    if (!showDeductionModal) return;
  
    const fetchExistingDeductions = async () => {
      try {
        const res = await fetch(`${API_URL}/api/attendance/payroll/${summary.id}/deductions`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            weekDays: weekDays.map((d) => d.format("YYYY-MM-DD")),
            user_id: user.id
          })
        });
        const json = await res.json();
  
        if (json.success && json.data) {
          const { type, amount, deductions } = json.data;
  
          if (type === "fixed") {
            // Fixed deduction case
            setDeductionType("fixed");
            setFixedDeduction(amount);
          } else {
            // Day-by-day case
            setDeductionType("dayByDay");
  
            const updated = weekDays.map((day) => {
              const dateStr = day.format("YYYY-MM-DD");
              const match = deductions.find((d) => d.date === dateStr);
  
              return {
                date: dateStr,
                day: day.format("dddd"),
                amount: match ? parseFloat(match.amount) : 0,
              };
            });
  
            setDayDeductions(updated);
          }
        }
      } catch (error) {
        console.error("Error loading existing deductions:", error);
      }
    };
  
    fetchExistingDeductions();
  }, [showDeductionModal]);

  const formatTime = (t) =>
    t ? moment(t, "HH:mm:ss").format("hh:mm A") : "--";
  const toNumber = (v) => (isNaN(v) || v === null ? 0 : Number(v));
  const formatMoney = (v) => `£${toNumber(v).toFixed(2)}`;
 
  const allDays = weekDays.map((day) => {
    const dateStr = day.format("YYYY-MM-DD");
    const found = records.find(
      (r) => r.attendance_date === dateStr || r.date === dateStr
    );

    if (!found) {
      return {
        date: dateStr,
        status: "absent",
        total_work_hours: 0,
        approved_hours: 0,
        overtime_pay_display: "--",
        overtime_details: null,
      };
    }

    const overtime = found.attendanceOvertimes?.[0]; // assuming one per day
    let overtime_pay_display = "--";
    let overtime_details = null;

    if (overtime && overtime.status === "approved") {
      if (overtime.fixed_overtime_amount != null) {
        const amount = parseFloat(overtime.fixed_overtime_amount);
        overtime_pay_display = <span className="text-success fw-bold">{formatMoney(amount)} <small>(fixed)</small></span>;
        overtime_details = { type: "fixed", amount };
      } else if (overtime.overtime_rate_per_hour != null) {
        const rate = parseFloat(overtime.overtime_rate_per_hour);
        const parts = overtime.total_duration.split(":");
        const h = parseInt(parts[0] || 0, 10);
        const m = parseInt(parts[1] || 0, 10);
        const s = parseInt(parts[2] || 0, 10);
        const hours = h + m / 60 + s / 3600;
        const total = rate * hours;
        overtime_pay_display = (
          <span className="text-primary fw-bold">
            {formatMoney(rate)}/hr × {hours.toFixed(2)}h = <span className="text-success">{formatMoney(total)}</span>
          </span>
        );
        overtime_details = { type: "hourly", rate, hours, total };
      }
    }

    return {
      ...found,
      overtime_pay_display,
      overtime_details,
    };
  });

  // Update record after edit

  const handleRecordUpdated = (updatedRecord) => {
    setRecords((prev) => {
      const getDate = (r) => r.attendance_date || r.date;
  
      const exists = prev.some((r) => {
        return r.id === updatedRecord.id || (!r.id && getDate(r) === getDate(updatedRecord));
      });
  
      let updatedRecords;
  
      if (exists) {
        updatedRecords = prev.map((r) => {
          if (r.id === updatedRecord.id || (!r.id && getDate(r) === getDate(updatedRecord))) {
            const originalOvertime = r.attendanceOvertimes?.[0];
            const hasApprovedOvertime = originalOvertime?.status === "approved";
  
            return {
              ...updatedRecord,
              attendanceOvertimes: hasApprovedOvertime ? [originalOvertime] : updatedRecord.attendanceOvertimes || [],
              overtime_pay_display: r.overtime_pay_display || "--",
              overtime_details: r.overtime_details || null,
            };
          }
          return r;
        });
      } else {
        updatedRecords = [...prev, updatedRecord];
      }
  
      // === RECALCULATE ALL TOTALS ===
      const totalWork = updatedRecords.reduce((sum, r) => sum + (parseFloat(r.total_work_hours) || 0), 0);
      const totalApproved = updatedRecords.reduce((sum, r) => sum + (parseFloat(r.approved_hours) || 0), 0);
      const totalDeduction = updatedRecords.reduce((sum, r) => sum + (parseFloat(r.deduction_amount) || 0), 0); // ← YE ADD KIYA
  
      const rate = parseFloat(user.rate) || 0;
      let baseWage = 0;
  
      const presentDays = updatedRecords.filter(
        (r) => r.status && !["absent", "schedule off"].includes(r.status)
      ).length;
  
      switch (user.payroll_type) {
        case "hourly":
          baseWage = totalApproved * rate;
          break;
        case "daily":
          baseWage = presentDays * rate;
          break;
        case "monthly":
          baseWage = rate;
          break;
        default:
          baseWage = totalApproved * rate;
      }
  
      // Final wage after deduction
      const finalWage = (baseWage - totalDeduction).toFixed(2);
  
      // Update summary
      setSummary((prevSummary) => ({
        ...prevSummary,
        total_hours: totalWork.toFixed(2),
        approved_hours: totalApproved.toFixed(2),
        total_deduction: totalDeduction.toFixed(2),        // ← YE DAAL DIYA
        wage: finalWage,    
        base_wage:baseWage,                              // ← YE BHI UPDATE
        presentDays,
      }));
  
      return updatedRecords;
    });
  };
  // 🧾 Export PDF
  const handleExportPDF = async () => {
    if (!tableRef.current) return;
    setIsGenerating(true);
  
    try {
      // 1️⃣ Clone only the table content (safe minimal node)
      const element = tableRef.current.cloneNode(true);
  
      // 2️⃣ Create a temporary wrapper (to isolate from Modals)
      const tempWrapper = document.createElement("div");
      tempWrapper.style.position = "fixed";
      tempWrapper.style.left = "-9999px";
      tempWrapper.style.top = "0";
      tempWrapper.style.background = "#fff";
      tempWrapper.style.width = "794px"; // A4 width
      tempWrapper.appendChild(element);
      document.body.appendChild(tempWrapper);
  
      // 3️⃣ Append signature block to cloned element
      const signatureDiv = document.createElement("div");
      signatureDiv.innerHTML = `
        <div style="
          display: flex;
          justify-content: space-between;
          margin-top: 60px;
          font-size: 14px;
          font-weight: 500;
        ">
          <div>
          ━━━━━━━━━━━━━━━━━━━━━━━ <br/>
            Signature of Employee
          </div>
          <div style="text-align:right;">
          ━━━━━━━━━━━━━━━━━━━━━━━  <br/>
            Administration Signature
          </div>
        </div>
      `;
      element.appendChild(signatureDiv);
  
      // 4️⃣ Generate canvas safely from detached clone
      const pdf = new jsPDF("p", "pt", "a4");
      const canvas = await html2canvas(element, {
        scale: 1.4,
        useCORS: true,
        backgroundColor: "#ffffff",
        removeContainer: true,
      });
  
      // 5️⃣ Clean up temp DOM
      tempWrapper.remove();
  
      // 6️⃣ Generate image + PDF
      const imgData = canvas.toDataURL("image/png");
      const imgWidth = 560;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
  
      pdf.addImage(imgData, "PNG", 25, 25, imgWidth, imgHeight);
      pdf.save(`Payroll_${user.name}_${startOfWeek.format("YYYYMMDD")}.pdf`);
    } catch (error) {
      console.error("PDF generation error:", error);
      alert("Error generating PDF. Please try again.");
    } finally {
      setIsGenerating(false);
    }
  };
  

  // 🔒 Lock Payroll
  const baseWage = parseFloat(summary.wage)  || 0; // Adjust back for display
  const tipValue = parseFloat(tipAmount) || 0;
  const finalWage = (baseWage + tipValue ).toFixed(2);
  const handleLockPayroll = async () => {
    if (addTip && (!tipAmount || tipAmount <= 0)) {
      alert("Please enter a valid tip amount.");
      return;
    }

    setIsLocking(true);
    const isLogin = JSON.parse(localStorage.getItem("user"));

    const payload = {
      user_id: isLogin?.id,
      tip_amount: addTip ? parseFloat(tipAmount) : 0,
      
    };

    try {
      const res = await fetch(
        `${API_URL}/api/attendance/payroll/${summary.id}/lock`,
        {
          method: "PUT",
          body: JSON.stringify(payload),
          headers: { "Content-Type": "application/json" },
        }
      );

      const json = await res.json();
      if (json.success) {
        setShowLockConfirm(false);
        Swal.fire({
          title:'Payroll locked',
          icon:'success',
          text:`Payroll locked successfully! ${
            addTip ? `Tip of ${formatMoney(tipAmount)} added.` : ""}`
          
        })
       
        window.location.reload();
      } else {
        
        Swal.fire({
          title:'Payroll locked',
          icon:'error',
          text:json.message || "Failed to lock payroll."
        })
      }
    } catch (e) {
      console.error("Lock error:", e);
      Swal.fire({
        title:'Payroll locked',
        icon:'error',
        text:"Error locking payroll. Please try again."
      })
      
    } finally {
      setIsLocking(false);
    }
  };
  const handleSubmitDeduction = async () => {
    if (deductionType === "fixed" && fixedDeduction <= 0) {
      // alert("Please enter a valid fixed deduction amount.");
      Swal.fire({
        title:'Amount Error',
        text:'Please enter a valid fixed deduction amount.',
        icon:'error'
      })
      return;
    }
  
    setIsSubmittingDeduction(true);
  
    let payload = {
      payroll_id: summary.id,
      type: deductionType,
    };
  
    if (deductionType === "fixed") {
      payload.amount = parseFloat(fixedDeduction);
    } else {
      // Remove zero amounts
      const filtered = dayDeductions.filter(d => d.amount > 0);
  
      payload.deductions = filtered;
    }
  
    try {
      const res = await fetch(`${API_URL}/api/attendance/payroll/deductions`, {
        method: "POST",
        body: JSON.stringify(payload),
        headers: { "Content-Type": "application/json" },
      });
  
      const json = await res.json();
  
      if (json.success) {
        const totalDeduction =
          deductionType === "fixed"
            ? parseFloat(fixedDeduction)
            : dayDeductions.reduce((sum, d) => sum + (d.amount || 0), 0);
  
        // Update summary wage UI only (backend stores actual value)
        setSummary((prev) => ({
          ...prev,
          total_deduction: totalDeduction,
          wage: (parseFloat(prev.base_wage) - totalDeduction).toFixed(2),
        }));
  
       
        Swal.fire({
          title:'Success',
          text:`Deductions applied: £${totalDeduction.toFixed(2)}`,
          icon:'success'
        })
        setShowDeductionModal(false);
      } else {
        Swal.fire({
          title:'Server Error',
          text: json.message || "Failed to apply deductions.",
          icon:'error'
        })
        
      }
    } catch (err) {
      console.error("Deduction error:", err);
      
      Swal.fire({
        title:'Server Error',
        text: "Error applying deductions.",
        icon:'error'
      })
    } finally {
      setIsSubmittingDeduction(false);
    }
  };
  
  // Handle day deduction change
  const handleDayDeductionChange = (index, value) => {
    setDayDeductions((prev) => {
      const updated = [...prev];
      updated[index] = {
        ...updated[index],
        amount: parseFloat(value) || 0,
      };
      return updated;
    });
  };
  if (!user)
    return <div className="alert alert-light">No payroll data found.</div>;

  return (
    <div className="card shadow-sm">
      <div className="card-header d-flex justify-content-between align-items-center">
      
        <h5 className="mb-0">
          Weekly Payroll ({startOfWeek.format("DD MMM")} -{" "}
          {endOfWeek.format("DD MMM YYYY")})
        </h5>
        <div className="d-flex gap-2">
        <PDFDownloadLink
  document={
    <PayrollPDFWeekly
      user={user}
      records={allDays}
      summary={summary}
      startOfWeek={startOfWeek}
      endOfWeek={endOfWeek}
    />
  }
  fileName={`Payroll_${user.name}_${startOfWeek.format("YYYY_MM_DD")}.pdf`}
>
  {({ loading }) => (
    <Button variant="outline-primary" disabled={loading}>
      {loading ? (
        <>
          <Spinner animation="border" size="sm" className="me-2" />
          Generating...
        </>
      ) : (
        <>
          <i className="bi bi-file-earmark-pdf me-2"></i>
          Download Weekly PDF
        </>
      )}
    </Button>
  )}
</PDFDownloadLink>
          {summary && (
            <>
              {!summary.locked && (
                <Button
                  variant="outline-warning"
                  onClick={() => setShowDeductionModal(true)}
                >
                  <i className="bi bi-scissors me-2"></i>Mark Deductions
                </Button>
              )}
              <Button
                variant={summary.locked ? "secondary" : "danger"}
                onClick={() => !summary.locked && setShowLockConfirm(true)}
                disabled={summary.locked}
              >
                <i className="bi bi-lock me-2"></i>
                {summary.locked ? "Locked" : "Lock Payroll"}
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Payroll Body */}
      <div className="card-body" ref={tableRef}>
        <div className="text-left mb-5">
          <img
            src="/assets/images/logo.png"
            alt="Company Logo"
            style={{ height: 60 }}
            className="mb-3"
          />

          <h6 className="fw-bold mb-1 text-uppercase">{user.name}</h6>
          <p className="mb-1 text-muted">
            <span className="fw-semibold text-dark">Rate:</span>{" "}
            <strong>
              {formatMoney(user.rate)}{" "}
              {user.payroll_type === "hourly"
                ? "/ hr"
                : user.payroll_type === "daily"
                ? "/ day"
                : user.payroll_type === "monthly"
                ? "/ month"
                : ""}
            </strong>
          </p>

          <p className="text-muted small mb-0">
            <span className="fw-semibold text-dark">Period:</span>{" "}
            {startOfWeek.format("DD MMM")} – {endOfWeek.format("DD MMM YYYY")}
          </p>
        </div>

        <div className="table-responsive">
          <Table bordered hover className="text-center align-middle">
            <thead className="custom-thead">
              <tr>
                <th>Day & Date</th>
                <th>Punch In</th>
                <th>Punch Out</th>
                <th>Work Hours</th>
                <th>Approved Hours</th>
                <th>Overtime Pay</th>
                <th>Status</th>
                {!summary?.locked && <th className="no-print">Action</th>}
              </tr>
            </thead>
            <tbody>
              {allDays.map((a) => (
                <tr key={a.date}>
                  <td className="text-start">
                    {moment(a.attendance_date || a.date).format("ddd, DD MMM")}
                  </td>
                  <td>{formatTime(a.punch_in_time)}</td>
                  <td>{formatTime(a.punch_out_time)}</td>
                  <td>{a.total_work_hours || 0}</td>
                  <td>{a.approved_hours || 0}</td>
                  <td>{a.overtime_pay_display || "--"}</td>
                  <td
                    className={
                      a.status === "absent"
                        ? "text-danger fw-bold"
                        : a.status === "late"
                        ? "text-warning fw-bold"
                        : "text-success fw-bold"
                    }
                  >
                    {a.status === "on-time" || a.status === "early"
                      ? "PRESENT"
                      : String(a.status).toUpperCase() || a.status}
                  </td>
                  {!summary?.locked && (
                    <td className="no-print">
                      {a.id ? (
                        <Button
                          size="sm"
                          variant="outline-dark"
                          onClick={() => setSelectedRecord(a)}
                        >
                          <Icon icon="simple-line-icons:pencil" />
                        </Button>
                      ) : a?.isWorkingDay ? (
                        <Button
                          size="sm"
                          variant="outline-success"
                          onClick={() =>
                            setSelectedRecord({
                              id: null,
                              user_id: user.id,
                              date: a.date,
                              status: "absent",
                              approved_hours: 0,
                            })
                          }
                        >
                          <Icon icon="bi:plus-lg" />
                        </Button>
                      ) : null}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
{/* ==================== PROFESSIONAL & CLEAN SUMMARY ==================== */}
{summary && (
 <div className="mt-5 pt-20 mb-20 border-top">
      <h5 className="mb-20 text-center fw-semibold text-dark">
        Payroll Summary
      </h5>

    <div className="row g-4">
      {/* First Row - Stats */}
      <div className="col-12">
        <div className="row g-3 text-center">
          <div className="col-6 col-md-3">
            <div className="p-3 bg-light border rounded">
              <div className="text-muted small">Total Hours</div>
              <div className="fw-bold fs-5 text-dark">
                {parseFloat(summary.total_hours || 0).toFixed(2)} <small className="text-muted">hr</small>
              </div>
            </div>
          </div>
          <div className="col-6 col-md-3">
            <div className="p-3 bg-light border rounded">
              <div className="text-muted small">Approved Hours</div>
              <div className="fw-bold fs-5 text-dark">
                {parseFloat(summary.approved_hours || 0).toFixed(2)}
              </div>
            </div>
          </div>
          <div className="col-6 col-md-3">
            <div className="p-3 bg-light border rounded">
              <div className="text-muted small">Overtime Hours</div>
              <div className="fw-bold fs-5 text-warning">
                {parseFloat(summary.total_overtime || 0).toFixed(2)}
              </div>
            </div>
          </div>
          <div className="col-6 col-md-3">
            <div className="p-3 bg-light border rounded">
              <div className="text-muted small">Present Days</div>
              <div className="fw-bold fs-5 text-success">
                {summary.presentDays || 0} / {summary.workingDays || 0}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Second Row - Financial Summary (Clean Table Style) */}
      <div className="col-12">
        <div className="border rounded overflow-hidden shadow-sm p-20">
          <table className="table table-borderless mb-0 align-middle">
            <tbody>
              <tr className="bg-light">
                <td className="ps-4 py-3 fw-medium text-muted">Base Wage</td>
                <td className="text-end pe-4 py-3 fw-bold text-dark">
                  {formatMoney(summary.base_wage)}
                </td>
              </tr>
              <tr>
                <td className="ps-4 py-3 fw-medium text-muted border-top">
                  + Overtime Pay
                </td>
                <td className="text-end pe-4 py-3 text-success fw-bold border-top">
                  + {formatMoney(summary.overtime_wage || 0)}
                </td>
              </tr>
              <tr>
                <td className="ps-4 py-3 fw-medium text-muted border-top">
                  - Deductions
                </td>
                <td className="text-end pe-4 py-3 text-danger fw-bold border-top">
                  - {formatMoney(summary.total_deduction || 0)}
                </td>
              </tr>
              <tr className="bg-primary text-white">
                <td className="ps-4 py-4 fw-semibold fs-5">
                  Net Payable Amount
                </td>
                <td className="text-end pe-4 py-4 fw-bold fs-4">
                  {formatMoney(summary.wage || summary.net_wage)}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Footer Info */}
      <div className="col-12 text-center text-muted small mt-3">
        <div className="d-flex justify-content-center gap-5 flex-wrap">
          <span><strong>Working Days:</strong> {summary.workingDays || 0}</span>
          <span><strong>Scheduled Off:</strong> {summary.offDays || 0}</span>
          <span><strong>Absent:</strong> {(summary.workingDays || 0) - (summary.presentDays || 0)}</span>
        </div>
      </div>
    </div>
  </div>
)}
      </div>

      {selectedRecord && (
        <EditAttendanceModal
          record={selectedRecord}
          onClose={() => setSelectedRecord(null)}
          onUpdated={handleRecordUpdated}
          employee={employee}
        />
      )}

<Modal
  show={showLockConfirm}
  onHide={() => {
    setShowLockConfirm(false);
    setAddTip(false);
    setTipAmount("");
  }}
  centered
  size="lg"
  backdrop="static"
>
  <Modal.Header closeButton className="border-0 pb-0">
    <Modal.Title className="d-flex align-items-center gap-3 w-100">
      <div className="bg-danger text-white rounded-circle p-3 d-flex align-items-center justify-content-center" style={{ width: '66px', height: '66px' }}>
      <Icon
                fontSize={70}
                 icon="mdi:lock"
                  className="menu-icon"
                />
      </div>
      <div>
        <h4 className="mb-0 fw-bold text-danger">Confirm Payroll Lock</h4>
        <small className="text-muted">This action is irreversible</small>
      </div>
    </Modal.Title>
  </Modal.Header>

  <Modal.Body className="pt-3">
    {/* Employee & Period */}
    <div className="bg-light rounded-3 p-10 mb-4 border-start border-primary ">
      <div className="row text-muted fw-medium">
        <div className="col-md-6 mb-3">
          <i className="bi bi-person-circle me-2 text-primary"></i>
          <strong className="text-dark">Employee:</strong> {user.name}
        </div>
        <div className="col-md-6 mb-3">
          <i className="bi bi-calendar-week me-2 text-primary"></i>
          <strong className="text-dark">Pay Period:</strong>{' '}
          {startOfWeek.format("DD MMM")} – {endOfWeek.format("DD MMM YYYY")}
        </div>
      </div>
    </div>

    {/* Summary Cards */}
    <h6 className="fw-bold text-secondary mb-3">
      <i className="bi bi-file-earmark-check me-2"></i>
      Payroll Summary
    </h6>
    <div className="row g-4 mb-4">
      {[
        { label: "Present Days", value: summary.presentDays, icon: "calendar-check", color: "text-success" },
        { label: "Working Days", value: summary.workingDays, icon: "briefcase" },
        { label: "Off Days", value: summary.offDays, icon: "moon", color: "text-info" },
        { label: "Total Work Hours", value: summary.total_hours, icon: "clock-history" },
        { label: "Approved Hours", value: summary.approved_hours, icon: "hand-thumbs-up", color: "text-success" },
        { label: "Total Overtime", value: summary.total_overtime, icon: "lightning-charge", color: "text-warning" },
      ].map((item, idx) => (
        <div className="col-6 col-lg-4" key={idx}>
          <div className="border rounded-3 p-3 bg-white shadow-sm h-100">
            <div className="d-flex align-items-center gap-2 mb-1">
              <i className={`bi bi-${item.icon} ${item.color || 'text-muted'}`}></i>
              <small className="text-muted">{item.label}</small>
            </div>
            <h5 className="mb-0 fw-bold text-dark">{item.value}</h5>
          </div>
        </div>
      ))}
    </div>

    {/* Wage Section with Tip */}
    <div className="border rounded-4 p-10 bg-light mb-4">
      <div className="row align-items-center mb-3">
        <div className="col-md-8">
          <strong className="fs-5">Gross</strong>
        </div>
        <div className="col-md-4 text-end">
          <p className="mb-0 text-dark">{formatMoney(summary.base_wage)}</p> {/* Show pre-deduction */}
        </div>
      </div>
      <div className="row align-items-center mb-3">
        <div className="col-md-8">
          <strong className="fs-5">Overtime Pay</strong>
        </div>
        <div className="col-md-4 text-end">
          <p className="mb-0 text-dark">{formatMoney(summary.overtime_wage)}</p> {/* Show pre-deduction */}
        </div>
      </div>
      <div className="row align-items-center mb-3">
        <div className="col-md-8">
          <strong className="fs-5">Deductions</strong>
        </div>
        <div className="col-md-4 text-end">
          <p className="mb-0 text-dark">{formatMoney(summary.total_deduction)}</p> {/* Show pre-deduction */}
        </div>
      </div>
      <div className="row align-items-center mb-3">
        <div className="col-md-8">
          <strong className="fs-5">Net</strong>
        </div>
        <div className="col-md-4 text-end">
          <h6 className="mb-0 text-dark">{formatMoney(summary.wage)}</h6> {/* Show pre-deduction */}
        </div>
      </div>
      

      {/* Add Tip Toggle */}
        
    
    </div>

    {/* Final Warning */}
    <div className="alert alert-warning border border-warning d-flex align-items-start gap-3 shadow-sm rounded-3">
      <i className="bi bi-exclamation-triangle-fill fs-3 text-warning flex-shrink-0 mt-1"></i>
      <div>
        <strong className="d-block mb-1">Irreversible Action</strong>
        Once locked, <u>no further edits</u> will be possible.<br />
      
        <span className="text-danger fw-semibold">Double-check all values before confirming.</span>
      </div>
    </div>
  </Modal.Body>

  <Modal.Footer className="border-0 pt-0">
    <Button
      variant="light"
      size="lg"
      onClick={() => {
        setShowLockConfirm(false);
        setAddTip(false);
        setTipAmount("");
      }}
      className="px-4"
      disabled={isLocking}
    >
      <i className="bi bi-arrow-left me-2"></i> Cancel
    </Button>
    <Button
      variant="danger"
      size="lg"
      onClick={handleLockPayroll}
      disabled={isLocking}
      className="px-5 shadow-sm"
    >
      {isLocking ? (
        <>
          <Spinner animation="border" size="sm" className="me-2" />
          Locking...
        </>
      ) : (
        <>
          <i className="bi bi-lock-fill me-2"></i>
          {addTip ? "Lock with Tip" : "Confirm & Lock Payroll"}
        </>
      )}
    </Button>
  </Modal.Footer>
</Modal>
{/* New Deduction Modal */}
{/* Deduction Modal - Updated Version */}
{/* Clean & Final Deduction Modal - Only One Deduction Column */}




<Modal show={showDeductionModal} onHide={() => setShowDeductionModal(false)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title><i className="bi bi-scissors text-warning me-2"></i>Mark Deductions</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form className="mb-4">
            <Form.Check type="radio" label="Fixed Deduction (Whole Week)" checked={deductionType === "fixed"} onChange={() => setDeductionType("fixed")} />
            <Form.Check type="radio" label="Day by Day Deductions" checked={deductionType === "dayByDay"} onChange={() => setDeductionType("dayByDay")} />
          </Form>

          {deductionType === "fixed" ? (
            <InputGroup className="mb-3">
              <InputGroup.Text>£</InputGroup.Text>
              <Form.Control type="number" min="0" step="0.01" value={fixedDeduction} onChange={(e) => setFixedDeduction(e.target.value)} />
            </InputGroup>
          ) : (
            <Table bordered hover>
              <thead className="table-danger text-white">
                <tr>
                  <th>Date</th>
                  <th>Status</th>
                  <th className="text-center">Deduction Amount (£)</th>
                </tr>
              </thead>
              <tbody>
                {weekDays.map((day, index) => {
                  const dateStr = day.format("YYYY-MM-DD");
                  const today = moment().format("YYYY-MM-DD");
                  const isFuture = day.isAfter(today, "day");
                  const canEdit = !isFuture;

                  const record = records.find((r) => (r.attendance_date || r.date) === dateStr);
                  if (record?.isWorkingDay === false) return null;

                  return (
                    <tr key={dateStr} className={!canEdit ? "table-secondary" : ""}>
                      <td>
                        <strong>{day.format("ddd, DD MMM")}</strong>
                        {day.isSame(today, "day") && <span className="badge bg-primary ms-2">Today</span>}
                        {isFuture && <span className="badge bg-warning ms-2">Future</span>}
                      </td>
                      <td>
                        {record?.status ? (
                          <span className={record.status === "absent" ? "text-danger fw-bold" : "text-success fw-bold"}>
                            {record.status.toUpperCase()}
                          </span>
                        ) : (
                          <span className="text-muted">—</span>
                        )}
                      </td>
                      <td className="text-center">
                        {canEdit ? (
                          <Form.Control
                            type="number"
                            min="0"
                            step="0.01"
                            value={dayDeductions[index]?.amount || 0}
                            onChange={(e) => handleDayDeductionChange(index, e.target.value)}
                            style={{ width: "120px", margin: "0 auto" }}
                            className="text-end"
                          />
                        ) : (
                          <span className="text-muted">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          )}

          <div className="alert alert-danger mt-4 text-center fw-bold fs-5">
            Total Deduction This Time: {formatMoney(
              deductionType === "fixed"
                ? fixedDeduction
                : dayDeductions.reduce((sum, d) => sum + (d.amount || 0), 0)
            )}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeductionModal(false)}>Cancel</Button>
          <Button variant="danger" onClick={handleSubmitDeduction} disabled={isSubmittingDeduction}>
            {isSubmittingDeduction ? "Applying..." : "Apply Deductions"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* New Deduction Modal */}
     {/* Bonus + Deduction Combined Modal */}

    </div>
  );
};

export default TimesheetPayrollSlipWeekly;